import React, { memo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { APIProvider, Map, RenderingType, useMap } from '@vis.gl/react-google-maps';

import { mapConfig } from '@constants/component-configs';
import { googleMapsApiKey, libraries, mapIds, version } from '@constants/google-maps';

import { getCurrentBicycle, getCurrentTraffic, getCurrentTransit, getCurrentViewport } from '@selectors/map-selector';

import './dotmaps-google-map.scss';

// The Map component's onClick/onDblclick/onMousemove hand back a plain {lat, lng} literal at
// event.detail.latLng, but callers throughout the app (map/index.js, measurement-utils.js) still
// expect the legacy google.maps.LatLng-shaped event.latLng, with .lat()/.lng()/.toJSON().
const toLegacyLatLng = literal => literal && {
  lat: () => literal.lat,
  lng: () => literal.lng,
  toJSON: () => ({ lat: literal.lat, lng: literal.lng })
};

const adaptMouseEvent = event => ({ ...event, latLng: toLegacyLatLng(event.detail?.latLng) });

// google.maps.TrafficLayer/BicyclingLayer/TransitLayer aren't wrapped by @vis.gl/react-google-maps,
// but they're unaffected by the AdvancedMarker deprecation, so we attach the native classes directly.
const NativeMapLayer = ({ active, create }) => {
  const map = useMap();

  useEffect(() => {
    if (!map || !active) {
      return void 0; // eslint-disable-line no-void
    }
    const layer = create();
    layer.setMap(map);
    return () => layer.setMap(null);
  }, [active, create, map]);

  return null;
};

NativeMapLayer.propTypes = {
  active: PropTypes.bool,
  create: PropTypes.func.isRequired
};

const MapLoadListener = ({ onMapLoad }) => {
  const map = useMap();

  useEffect(() => {
    if (map) {
      onMapLoad?.(map);
    }
  }, [map, onMapLoad]);

  return null;
};

MapLoadListener.propTypes = {
  onMapLoad: PropTypes.func
};

// Pans/zooms the map imperatively when `position` changes (e.g. a search result was picked -
// see focusMap() in map-actions.js). center/zoom on <Map> are intentionally uncontrolled
// (defaultCenter/defaultZoom below) so free user panning/zooming isn't fought by React; this is
// the one legitimate case that still needs to move the map programmatically.
const MapFocusListener = ({ position, zoom }) => {
  const map = useMap();

  useEffect(() => {
    if (!map || !position) {
      return;
    }
    map.panTo(position);
    if (zoom) {
      map.setZoom(zoom);
    }
    // `position` itself deliberately isn't a dep - it's a plain object that gets a new
    // reference on every store update, and we only want to react to its lat/lng changing.
  }, [map, position?.lat, position?.lng, zoom]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

MapFocusListener.propTypes = {
  position: PropTypes.object,
  zoom: PropTypes.number
};

// Google Maps' own 'mousemove' map event stops firing while the cursor is over any clickable
// overlay (documented Maps API behavior - markers/polygons/polylines absorb it). Legacy Markers
// were canvas-rendered with per-pixel hit-testing (only opaque icon pixels blocked it), but
// AdvancedMarkerElement is real DOM, so its whole rectangular bounding box blocks the event -
// with markers now covering much more of the map, the highlight-follow-circle and measurement
// tool tracking (both driven by mousemove) would constantly freeze under them. Bypass the Maps
// API event: track native pointermove on the map's own div in the capture phase (runs before any
// overlay's listener can stop propagation) and convert pixel -> LatLng via a persistent,
// invisible OverlayView projection.
const MouseTracker = ({ onMouseMove }) => {
  const map = useMap();

  useEffect(() => {
    if (!map || !onMouseMove) {
      return void 0; // eslint-disable-line no-void
    }
    const projectionOverlay = new window.google.maps.OverlayView();
    projectionOverlay.onAdd = () => {};
    projectionOverlay.draw = () => {};
    projectionOverlay.onRemove = () => {};
    projectionOverlay.setMap(map);

    const container = map.getDiv();
    const handlePointerMove = domEvent => {
      const projection = projectionOverlay.getProjection();
      if (!projection) {
        return;
      }
      const rect = container.getBoundingClientRect();
      const point = new window.google.maps.Point(domEvent.clientX - rect.left, domEvent.clientY - rect.top);
      const latLng = projection.fromContainerPixelToLatLng(point);
      if (latLng) {
        onMouseMove({ latLng, domEvent });
      }
    };
    container.addEventListener('pointermove', handlePointerMove, true);

    return () => {
      container.removeEventListener('pointermove', handlePointerMove, true);
      projectionOverlay.setMap(null);
    };
  }, [map, onMouseMove]);

  return null;
};

MouseTracker.propTypes = {
  onMouseMove: PropTypes.func
};

// Defined once at module scope (rather than inline in render) so NativeMapLayer's effect,
// keyed on `create`, doesn't tear down and recreate these on every DotMapsGoogleMap render
// (which happens on every pan/zoom, since it reads the viewport from the store).
const createTrafficLayer = () => new window.google.maps.TrafficLayer();
const createBicyclingLayer = () => new window.google.maps.BicyclingLayer();
const createTransitLayer = () => new window.google.maps.TransitLayer();

const containerStyle = { height: '100%', width: '100%' };

const DotMapsGoogleMap = ({
  components,
  onBoundsChanged,
  onClick,
  onDblClick,
  onDragEnd,
  onDragStart,
  onIdle,
  onMapLoad,
  onMouseMove
}) => {
  const mapTypeId = useSelector(state => state.map.mapTypeId);
  const bicycle = useSelector(getCurrentBicycle);
  const traffic = useSelector(getCurrentTraffic);
  const transit = useSelector(getCurrentTransit);
  const poisOn = useSelector(state => state.map.markers.poisOn);
  const { center, defaultMarkerPosition, zoom } = useSelector(getCurrentViewport);

  // A vector Map ID's style can't be swapped on a live map instance - the Map component
  // has to be remounted (hence `key`) to pick up the new one. See DOT-4736 memory notes.
  const mapId = poisOn ? mapIds.poisOn : mapIds.default;

  return (
    <div data-testid="google-map" styleName="map-wrapper">
      <div styleName="map-element">
        <APIProvider apiKey={googleMapsApiKey} libraries={libraries} version={version}>
          {/* gestureHandling defaults to 'auto', which resolves to 'cooperative' (requiring
              ctrl+scroll to zoom) in this embedding structure - 'greedy' restores plain
              mouse-wheel zoom, matching the old library's behavior. */}
          <Map
            {...mapConfig.mapOptions}
            key={mapId}
            defaultCenter={center}
            defaultZoom={zoom}
            gestureHandling="greedy"
            mapId={mapId}
            mapTypeId={mapTypeId}
            onBoundsChanged={onBoundsChanged}
            onClick={event => onClick?.(adaptMouseEvent(event))}
            onDblclick={event => onDblClick?.(adaptMouseEvent(event))}
            onDragend={onDragEnd}
            onDragstart={onDragStart}
            onIdle={onIdle}
            renderingType={RenderingType.VECTOR}
            reuseMaps
            style={containerStyle}
          >
            <MapLoadListener onMapLoad={onMapLoad} />
            <MapFocusListener position={defaultMarkerPosition} zoom={zoom} />
            <MouseTracker onMouseMove={onMouseMove} />
            <NativeMapLayer active={traffic} create={createTrafficLayer} />
            <NativeMapLayer active={bicycle} create={createBicyclingLayer} />
            <NativeMapLayer active={transit} create={createTransitLayer} />
            {components}
          </Map>
        </APIProvider>
      </div>
    </div>
  );
};

DotMapsGoogleMap.propTypes = {
  components: PropTypes.node,
  onBoundsChanged: PropTypes.func,
  onClick: PropTypes.func,
  onDblClick: PropTypes.func,
  onDragEnd: PropTypes.func,
  onDragStart: PropTypes.func,
  onIdle: PropTypes.func,
  onMapLoad: PropTypes.func,
  onMouseMove: PropTypes.func
};

export default memo(DotMapsGoogleMap);
